import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import DashboardAddForm from 'components/DashboardAddForm/DashboardAddForm';
import DashboardsModalWrapper from 'components/DashboardsModalWrapper/DashboardsModalWrapper';
import DashboardList from 'components/DashboardList/DashboardList';
import { filterDashboardsByTitle } from 'routes/Library/helpers';
import { trackEvent } from 'helpers/trackerHelpers';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchActiveDashboards } from 'routes/AllDashboards/hooks/getDashboardsList';
import { useExploreContext } from 'routes/Explore/components/Contexts/ExploreContext';
import { DashboardT } from 'business/definitions';
import { useSelector } from 'store/hooks';
import { SettingsState } from 'routes/Explore/hooks/reducers/settingsReducer';
import AddToDashboardItem from './AddToDashboardItem';
import { useAddChartToDashboard } from './hooks/addChartToDashboard';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import { WidgetPositionContextType } from 'lib/layout/context/WidgetPositionContext';
import { useDashboardWidgetContext } from 'routes/Dashboard/components/WidgetsLayout/context/WidgetContext';

type AddToDashboardModalProps = {
    show: boolean;
    closeModal: () => void;
    chartKey?: number | null;
    settings?: Pick<
        SettingsState,
        'chartTitle' | 'selectedViz' | 'chartSettings'
    >;
    isInDashboard?: boolean;
    query?: ExploreQueryT;
    widgetPosition?: WidgetPositionContextType;
};

const AddToDashboardModal = ({
    show,
    chartKey = undefined,
    closeModal,
    settings = undefined,
    isInDashboard = undefined,
    query = undefined,
    widgetPosition = undefined,
}: AddToDashboardModalProps): JSX.Element => {
    const { t } = useTranslation();
    const { explore } = useExploreContext();
    const queryClient = useQueryClient();
    const [shouldInsertChartBelow, setShouldInsertChartBelow] =
        useState<boolean>(
            () => localStorage.getItem('shouldInsertChartBelow') === 'true',
        );

    const toggleInsertChartBelow = () => {
        setShouldInsertChartBelow((prev) => {
            const newValue = !prev;
            localStorage.setItem('shouldInsertChartBelow', newValue.toString());
            return newValue;
        });
    };

    const homeDashboardSlug = useSelector(
        (state) => state.vision.user.homeDashboardSlug,
    );
    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const { data: activeDashboards } = useFetchActiveDashboards();
    // Only show dashboard where we have edit right
    const dashboards = activeDashboards
        ? activeDashboards.filter((d) => d.canEdit)
        : [];

    const { key: widgetKey } = useDashboardWidgetContext();
    const { addChartToDashboard } = useAddChartToDashboard();

    const addChart = (dashboard: DashboardT) =>
        addChartToDashboard(
            explore,
            dashboard,
            chartKey,
            widgetKey,
            isInDashboard,
            query,
            settings,
            undefined,
            undefined,
            currentDashboard?.key === dashboard.key && shouldInsertChartBelow
                ? widgetPosition
                : undefined,
        );

    // TODO we need to filter out dashboards that we cannot edit using canEditDashboard

    /* Handle dashboards selection */
    const [selection, setSelection] = useState<string[]>([]);
    const submit = () => {
        selection.forEach((slug) => {
            const dashboard = dashboards.find((dash) => dash.slug === slug);
            if (dashboard !== undefined) {
                addChart(dashboard);
                trackEvent('decision/indicator/saved', { chartKey });
                queryClient.invalidateQueries({
                    queryKey: ['widget', chartKey],
                });
            }
        });
        closeModal();
    };

    /* Handle the selection inside the library. We useCallback to avoid reference issues */
    const changeSelection = useCallback(
        (slug: string) => {
            let newSelection;
            if (selection.includes(slug)) {
                newSelection = selection.filter((s) => s !== slug);
            } else {
                newSelection = [...selection, slug];
            }
            setSelection(newSelection);
        },
        [selection],
    );
    const [filterString, setFilterString] = useState('');
    const filteredDashboards = filterDashboardsByTitle(
        dashboards,
        filterString,
        [],
    );
    const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
        setFilterString(e.target.value);

    const onCreateDashboard = (dashboard: DashboardT) => {
        addChart(dashboard);
        trackEvent('decision/indicator/saved', { chartKey });
        closeModal();
    };

    const addToDashboardItem = (dashboard: DashboardT) => (
        <AddToDashboardItem
            key={dashboard.slug}
            dashboard={dashboard}
            isSelected={selection.includes(dashboard.slug)}
            changeSelection={changeSelection}
            isCurrentDashboard={currentDashboard?.key === dashboard.key}
            shouldInsertChartBelow={shouldInsertChartBelow}
            toggleInsertChartBelow={toggleInsertChartBelow}
        />
    );

    return (
        <DashboardsModalWrapper open={show} onClose={closeModal}>
            <DialogContent>
                <TextField
                    value={filterString}
                    onChange={onChangeSearch}
                    autoFocus
                    variant="outlined"
                    margin="dense"
                    size="small"
                    fullWidth
                    label={t('dashboards.search')}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <DashboardAddForm onCreate={onCreateDashboard} />
                {!!dashboards.length && (
                    <DashboardList
                        dashboards={filteredDashboards}
                        homeDashboardSlug={homeDashboardSlug}
                        dashboardItemRender={(dashboard) =>
                            addToDashboardItem(dashboard)
                        }
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={closeModal}
                    className="e2e-library-modal-close"
                >
                    {t('common.close')}
                </Button>
                <Button
                    data-testid="e2e-library-modal-validate"
                    onClick={submit}
                    className="e2e-library-modal-validate"
                >
                    {t('common.add')}
                </Button>
            </DialogActions>
        </DashboardsModalWrapper>
    );
};

export default AddToDashboardModal;
